/**
 * Service Worker Control — limpeza de SW/cache herdados.
 *
 * Modo normal:
 * - limpeza no bootstrap
 * - limpeza ao focar / voltar online / voltar a aba
 *
 * Modo preview-safe:
 * - limpeza única no bootstrap (sem listeners contínuos)
 * - evita drift de versão sem interferir com o fluxo tokenizado do preview
 */

import { debugLog } from './debugLogger';

type ServiceWorkerControlOptions = {
  previewSafeMode?: boolean;
};

async function clearAllCaches() {
  if (!("caches" in window)) return 0;

  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((name) => caches.delete(name)));

  if (cacheNames.length > 0) {
    debugLog('build', '[NetUno:PWA] all_caches_cleared', { caches: cacheNames });
  }

  return cacheNames.length;
}

async function unregisterAllServiceWorkers() {
  if (!("serviceWorker" in navigator)) return 0;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));

  if (registrations.length > 0) {
    debugLog('build', '[NetUno:PWA] service_workers_unregistered', {
      count: registrations.length,
      scopes: registrations.map((r) => r.scope),
    });
  }

  return registrations.length;
}

export function installServiceWorkerControl(options: ServiceWorkerControlOptions = {}) {
  if (typeof window === "undefined") return () => undefined;

  const { previewSafeMode = false } = options;

  const purgeLegacyWorkers = async () => {
    try {
      const [unregisteredCount, clearedCount] = await Promise.all([
        unregisterAllServiceWorkers(),
        clearAllCaches(),
      ]);

      debugLog('build', '[NetUno:PWA] purge_executed', {
        previewSafeMode,
        unregisteredCount,
        clearedCount,
      });
    } catch (error) {
      console.warn("[ServiceWorkerControl] Falha ao limpar SW/caches:", error);
    }
  };

  void purgeLegacyWorkers();

  if (previewSafeMode) {
    debugLog('build', '[NetUno:PWA] service_worker_control_installed_preview_safe');
    return () => undefined;
  }

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      void purgeLegacyWorkers();
    }
  };

  window.addEventListener("online", purgeLegacyWorkers);
  window.addEventListener("focus", purgeLegacyWorkers);
  document.addEventListener("visibilitychange", onVisible);

  debugLog('build', '[NetUno:PWA] service_worker_control_installed');

  return () => {
    window.removeEventListener("online", purgeLegacyWorkers);
    window.removeEventListener("focus", purgeLegacyWorkers);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
